import React from 'react';
import { motion } from 'framer-motion';
import { 
  Target, CheckCircle, Clock, Zap, TrendingUp, Calendar, 
  Coffee, Briefcase, Timer, Award, Flame, BookOpen, Trophy, ArrowLeft
} from 'lucide-react';
import { 
  LineChart, Line, AreaChart, Area, XAxis, YAxis, ResponsiveContainer, 
  Tooltip, BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';
import { useDisciplineStore } from '../../contexts/disciplineStore';

const ProductivityStatsComponent = ({ isDarkMode = true, onBackToOverview }) => {
  // Get real data from Digital Discipline store
  const { goals, timers, xp, streak, completedGoals } = useDisciplineStore();
  
  // Calculate real productivity metrics
  const productivityData = {
    tasksCompleted: Array.isArray(completedGoals) ? completedGoals.length : goals.filter(g => g.completed).length,
    tasksTotal: goals.length,
    focusMinutes: timers.reduce((total, timer) => {
      if (timer.isCompleted) {
        return total + Math.round((timer.duration || 25) / 60); // Convert to minutes
      }
      return total;
    }, 0),
    pomodoroCycles: Math.floor(timers.filter(t => t.isCompleted).length),
    projectsActive: goals.filter(g => !g.completed && g.category).length,
    projectsCompleted: goals.filter(g => g.completed).length,
    efficiency: goals.length > 0 ? Math.round((goals.filter(g => g.completed).length / goals.length) * 100) : 0,
    streakDays: streak,
    totalXP: xp
  };

  // Generate weekly data based on completed timers
  const weeklyTasks = React.useMemo(() => {
    const days = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
    const now = new Date();
    
    return days.map((day, index) => {
      const date = new Date(now);
      date.setDate(now.getDate() - (6 - index));
      
      // Filter timers for this day
      const dayTimers = timers.filter(timer => {
        if (!timer.startTime) return false;
        const timerDate = new Date(timer.startTime);
        return timerDate.toDateString() === date.toDateString();
      });
      
      const completed = dayTimers.filter(t => t.isCompleted).length;
      const total = dayTimers.length;
      const focus = dayTimers.reduce((sum, t) => {
        if (t.isCompleted) {
          return sum + Math.round((t.duration || 25) / 60); // Convert to minutes
        }
        return sum;
      }, 0);
      
      return { day, completed, total: Math.max(completed, total), focus };
    });
  }, [timers]);

  // Generate hourly focus data based on completed timers
  const focusTime = React.useMemo(() => {
    const hours = ['09:00', '10:00', '11:00', '12:00', '14:00', '15:00', '16:00', '17:00'];
    
    return hours.map(time => {
      const hour = parseInt(time.split(':')[0]);
      
      // Filter timers completed in this hour
      const hourTimers = timers.filter(timer => {
        if (!timer.startTime || !timer.isCompleted) return false;
        const timerHour = new Date(timer.startTime).getHours();
        return timerHour === hour;
      });
      
      // Calculate focus percentage based on completed vs failed timers
      const totalHourTimers = hourTimers.length;
      const completedHourTimers = hourTimers.filter(t => t.isCompleted).length;
      
      let focus = 70; // Default baseline
      if (totalHourTimers > 0) {
        focus = Math.round((completedHourTimers / totalHourTimers) * 100);
      }
      
      // Add some variation based on time of day
      if (hour >= 9 && hour <= 11) focus += 10; // Morning boost
      if (hour === 12) focus -= 25; // Lunch dip
      if (hour >= 14 && hour <= 16) focus += 5; // Afternoon focus
      
      focus = Math.min(95, Math.max(30, focus)); // Clamp between 30-95
      const distraction = 100 - focus;
      
      return { time, focus, distraction };
    });
  }, [timers]);

  // Calculate daily average tasks based on real data
  const dailyAverageCalc = React.useMemo(() => {
    if (timers.length === 0) return 0;
    
    // Get unique days when timers were used
    const activeDays = new Set();
    timers.forEach(timer => {
      if (timer.startTime) {
        const day = new Date(timer.startTime).toDateString();
        activeDays.add(day);
      }
    });
    
    const dayCount = Math.max(1, activeDays.size);
    const completedTimers = timers.filter(t => t.isCompleted).length;
    
    return (completedTimers / dayCount).toFixed(1);
  }, [timers]);

  // Generate project categories from real goal data
  const projectCategories = React.useMemo(() => {
    const categoryCount = {};
    const colors = {
      productivity: '#3b82f6',
      health: '#10b981', 
      learning: '#f59e0b',
      mindfulness: '#8b5cf6',
      wellness: '#ef4444',
      other: '#6b7280'
    };

    goals.forEach(goal => {
      const category = goal.category || 'other';
      const categoryName = category === 'productivity' ? 'İş' :
                          category === 'health' ? 'Sağlık' :
                          category === 'learning' ? 'Öğrenme' :
                          category === 'mindfulness' ? 'Farkındalık' :
                          category === 'wellness' ? 'Wellness' :
                          'Diğer';
      
      if (!categoryCount[category]) {
        categoryCount[category] = {
          name: categoryName,
          value: 0,
          color: colors[category] || colors.other
        };
      }
      categoryCount[category].value++;
    });

    return Object.values(categoryCount).filter(cat => cat.value > 0);
  }, [goals]);

  // Generate recent achievements based on real data
  const recentAchievements = React.useMemo(() => {
    const achievements = [];
    
    // Check for focus master achievement (2+ hours in one session)
    const longSessions = timers.filter(timer => {
      return timer.isCompleted && (timer.duration || 25) >= 120; // 2+ hours
    });
    if (longSessions.length > 0) {
      const latest = longSessions[longSessions.length - 1];
      const timeAgo = latest.startTime ? 
        Math.floor((Date.now() - new Date(latest.startTime)) / (1000 * 60 * 60)) : 0;
      achievements.push({
        title: 'Odak Ustası',
        desc: `${Math.round((latest.duration || 25) / 60)} saat kesintisiz çalışma`,
        date: timeAgo < 24 ? `${timeAgo} saat önce` : `${Math.floor(timeAgo / 24)} gün önce`,
        color: 'blue'
      });
    }
    
    // Check for task completer (5+ tasks in one day)
    const today = new Date().toDateString();
    const todayCompletedTasks = timers.filter(timer => {
      return timer.isCompleted && 
             timer.startTime && 
             new Date(timer.startTime).toDateString() === today;
    }).length;
    
    if (todayCompletedTasks >= 5) {
      achievements.push({
        title: 'Görev Tamamlayıcı',
        desc: `${todayCompletedTasks} görev tek günde`,
        date: 'Bugün',
        color: 'green'
      });
    }
    
    // Check for streak achievement
    if (streak >= 7) {
      achievements.push({
        title: 'Kararlı Çalışan',
        desc: `${streak} gün üst üste aktif`,
        date: 'Aktif seri',
        color: 'purple'
      });
    }
    
    // Add default achievements if none exist
    if (achievements.length === 0) {
      achievements.push(
        { title: 'Başlangıç', desc: 'İlk hedefinizi oluşturun', date: 'Yakında', color: 'blue' },
        { title: 'İlk Adım', desc: 'İlk timer oturumunu tamamlayın', date: 'Yakında', color: 'green' }
      );
    }
    
    return achievements.slice(0, 3); // Show max 3 achievements
  }, [timers, streak]);

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 300, damping: 24 }
    }
  };

  const StatCard = ({ title, value, unit, icon, color, trend, subtitle }) => (
    <motion.div 
      className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}
      variants={itemVariants}
      whileHover={{ y: -2 }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center">
          <div className={`p-2 rounded-lg`} style={{ backgroundColor: color + '20' }}>
            {React.cloneElement(icon, { size: 20, color })}
          </div>
          <h3 className="ml-3 font-medium text-sm">{title}</h3>
        </div>
        {trend && (
          <div className={`flex items-center text-xs ${trend > 0 ? 'text-green-500' : 'text-red-500'}`}>
            <TrendingUp size={12} className={trend < 0 ? 'rotate-180' : ''} />
            <span className="ml-1">{Math.abs(trend)}%</span>
          </div>
        )}
      </div>
      
      <div className="mb-1">
        <span className="text-2xl font-bold">{value}</span>
        <span className={`text-sm ml-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {unit}
        </span>
      </div>
      
      {subtitle && (
        <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {subtitle}
        </p>
      )}
    </motion.div>
  );
  return (
    <div className="space-y-6">
      {/* Back Button */}
      {onBackToOverview && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <button
            onClick={onBackToOverview}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
              isDarkMode 
                ? 'bg-gray-800 hover:bg-gray-700 text-gray-200' 
                : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
            }`}
          >
            <ArrowLeft className="h-4 w-4" />
            <span>İstatistiklere Geri Dön</span>
          </button>
        </motion.div>
      )}
      
      {/* Productivity Overview Cards */}
      <motion.div 
        className="grid grid-cols-2 md:grid-cols-4 gap-4"
        initial="hidden"
        animate="visible"
        variants={{
          visible: {
            transition: {
              staggerChildren: 0.1
            }
          }
        }}
      >
        <StatCard 
          title="Tamamlanan Görevler"
          value={productivityData.tasksCompleted}
          unit={`/ ${productivityData.tasksTotal}`}
          icon={<CheckCircle />}
          color="#10b981"
          trend={productivityData.tasksTotal > 0 ? Math.round((productivityData.tasksCompleted / productivityData.tasksTotal) * 100 - 75) : 0}
          subtitle="Toplam"
        />
        
        <StatCard 
          title="Odaklanma Süresi"
          value={productivityData.focusMinutes}
          unit="dakika"
          icon={<Timer />}
          color="#3b82f6"
          trend={productivityData.focusMinutes > 120 ? 15 : productivityData.focusMinutes > 60 ? 8 : -5}
          subtitle="Tamamlanan"
        />
        
        <StatCard 
          title="Aktif Projeler"
          value={productivityData.projectsActive}
          unit="proje"
          icon={<Briefcase />}
          color="#f59e0b"
          trend={productivityData.projectsActive > 3 ? 10 : 0}
          subtitle="Devam ediyor"
        />
        
        <StatCard 
          title="Verimlilik Skoru"
          value={productivityData.efficiency}
          unit="%"
          icon={<Target />}
          color="#8b5cf6"
          trend={5}
          subtitle="Haftalık ortalama"
        />
      </motion.div>

      {/* Weekly Task Completion */}
      <motion.div 
        className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}
        variants={itemVariants}
      >
        <h3 className="text-lg font-bold mb-4 flex items-center">
          <CheckCircle className="h-5 w-5 mr-2 text-green-500" />
          Haftalık Görev Tamamlama
        </h3>
        
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={weeklyTasks}>
              <XAxis dataKey="day" tick={{ fill: isDarkMode ? '#d1d5db' : '#4b5563' }} />
              <YAxis tick={{ fill: isDarkMode ? '#d1d5db' : '#4b5563' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                  borderColor: isDarkMode ? '#374151' : '#e5e7eb',
                  color: isDarkMode ? '#f3f4f6' : '#1f2937'
                }}
              />
              <Bar dataKey="completed" fill="#10b981" name="Tamamlanan" />
              <Bar dataKey="total" fill="#6b7280" name="Toplam" opacity={0.3} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </motion.div>

      {/* Focus Time Chart */}
      <motion.div 
        className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}
        variants={itemVariants}
      >
        <h3 className="text-lg font-bold mb-4 flex items-center">
          <Zap className="h-5 w-5 mr-2 text-yellow-500" />
          Günlük Odaklanma Dağılımı
        </h3>
        
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={focusTime}>
              <XAxis dataKey="time" tick={{ fill: isDarkMode ? '#d1d5db' : '#4b5563' }} />
              <YAxis tick={{ fill: isDarkMode ? '#d1d5db' : '#4b5563' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                  borderColor: isDarkMode ? '#374151' : '#e5e7eb',
                  color: isDarkMode ? '#f3f4f6' : '#1f2937'
                }}
              />
              <Area 
                type="monotone" 
                dataKey="focus" 
                stackId="1"
                stroke="#3b82f6" 
                fill="#3b82f6" 
                fillOpacity={0.6}
                name="Odaklanma %"
              />
              <Area 
                type="monotone" 
                dataKey="distraction" 
                stackId="1"
                stroke="#ef4444" 
                fill="#ef4444" 
                fillOpacity={0.6}
                name="Dikkat Dağınıklığı %"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </motion.div>

      {/* Project Distribution */}
      <motion.div 
        className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}
        variants={itemVariants}
      >
        <h3 className="text-lg font-bold mb-4 flex items-center">
          <Briefcase className="h-5 w-5 mr-2 text-purple-500" />
          Proje Dağılımı
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={projectCategories}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {projectCategories.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          <div className="space-y-4">
            {projectCategories.map((category, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div 
                    className="w-3 h-3 rounded-full mr-3"
                    style={{ backgroundColor: category.color }}
                  />
                  <span className="text-sm">{category.name}</span>
                </div>
                <span className="font-medium">{category.value}</span>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Productivity Metrics */}
      <motion.div 
        className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}
        variants={itemVariants}
      >
        <h3 className="text-lg font-bold mb-4 flex items-center">
          <Award className="h-5 w-5 mr-2 text-amber-500" />
          Verimlilik Metrikleri
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <div className="flex items-center mb-2">
              <Flame className="h-4 w-4 mr-2 text-orange-500" />
              <p className={`text-xs opacity-70`}>Seri</p>
            </div>
            <p className="text-lg font-medium">{productivityData.streakDays} gün</p>
          </div>
          
          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <div className="flex items-center mb-2">
              <Briefcase className="h-4 w-4 mr-2 text-blue-500" />
              <p className={`text-xs opacity-70`}>Aktif Projeler</p>
            </div>
            <p className="text-lg font-medium">{productivityData.projectsActive}</p>
          </div>
          
          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <div className="flex items-center mb-2">
              <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
              <p className={`text-xs opacity-70`}>Tamamlanan</p>
            </div>
            <p className="text-lg font-medium">{productivityData.projectsCompleted}</p>
          </div>
          
          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <div className="flex items-center mb-2">
              <BookOpen className="h-4 w-4 mr-2 text-purple-500" />
              <p className={`text-xs opacity-70`}>Ortalama/Gün</p>
            </div>
            <p className="text-lg font-medium">{dailyAverageCalc} görev</p>
          </div>
        </div>
      </motion.div>

      {/* Recent Achievements */}
      <motion.div 
        className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}
        variants={itemVariants}
      >
        <h3 className="text-lg font-bold mb-4 flex items-center">
          <Trophy className="h-5 w-5 mr-2 text-yellow-500" />
          Son Başarılar
        </h3>
        
        <div className="space-y-3">
          {recentAchievements.map((achievement, index) => (
            <div key={index} className={`p-3 rounded-lg border-l-4 ${
              achievement.color === 'blue' ? 'border-blue-500' :
              achievement.color === 'green' ? 'border-green-500' : 'border-purple-500'
            } ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-medium text-sm">{achievement.title}</h4>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {achievement.desc}
                  </p>
                </div>
                <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {achievement.date}
                </span>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default ProductivityStatsComponent;
